import { ApolloError, gql, useMutation } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import clientSimulator from "utils/clientSimulator";
import useNotifier from "hooks/useNotifier";
import { handleGQLErrors } from "utils/gqlErrors";
import {
  AvatarCsvExportStatus,
  AvatarCsvExportStatusVariables,
  StartAvatarCsvExport,
  StartAvatarCsvExportVariables,
} from "./types/AvatarCsvExport";

/** Avatar attr CSV export only: federated gateway (clientSimulator / FEDERATION_API), not thingMakeSimulator. */

const POLL_MS = 1500;
const MAX_POLLS = 800;

export const avatarCsvExportSessionKey = (batchId: string) =>
  `avatarCsvExport:${batchId}`;

interface AvatarCsvExportSession {
  exportId: string;
  startedAt: number;
}

function readAvatarCsvExportSession(
  batchId: string
): AvatarCsvExportSession | null {
  try {
    const raw = sessionStorage.getItem(avatarCsvExportSessionKey(batchId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AvatarCsvExportSession;
    if (typeof parsed?.exportId !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveAvatarCsvExportSession(batchId: string, exportId: string): void {
  sessionStorage.setItem(
    avatarCsvExportSessionKey(batchId),
    JSON.stringify({ exportId, startedAt: Date.now() } satisfies AvatarCsvExportSession)
  );
}

function clearAvatarCsvExportSession(batchId: string): void {
  sessionStorage.removeItem(avatarCsvExportSessionKey(batchId));
}

export const startAvatarCsvExportMutation = gql`
  mutation StartAvatarCsvExport($input: StartAvatarCsvExportInput!) {
    startAvatarCsvExport(input: $input) {
      exportId
    }
  }
`;

export const avatarCsvExportStatusQuery = gql`
  query AvatarCsvExportStatus($exportId: String!) {
    avatarCsvExportStatus(exportId: $exportId) {
      exportId
      status
      processedRows
      downloadUrl
      s3Key
      errorMessage
    }
  }
`;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useBatchAvatarCsvExport(batchId: string) {
  const notify = useNotifier();
  const [starting, setStarting] = useState(false);
  const pollGeneration = useRef(0);

  const [mutate] = useMutation<StartAvatarCsvExport, StartAvatarCsvExportVariables>(
    startAvatarCsvExportMutation,
    { client: clientSimulator }
  );

  const pollUntilDone = useCallback(
    async (exportId: string, generation: number) => {
      let lastProgressRows = -1;
      for (let i = 0; i < MAX_POLLS; i++) {
        if (generation !== pollGeneration.current) {
          return;
        }
        const { data, errors } = await clientSimulator.query<
          AvatarCsvExportStatus,
          AvatarCsvExportStatusVariables
        >({
          query: avatarCsvExportStatusQuery,
          variables: { exportId },
          fetchPolicy: "network-only",
        });
        if (generation !== pollGeneration.current) {
          return;
        }
        if (errors?.length) {
          handleGQLErrors(
            notify,
            new ApolloError({ graphQLErrors: errors })
          );
          return;
        }
        const s = data?.avatarCsvExportStatus;
        if (!s) {
          clearAvatarCsvExportSession(batchId);
          notify({ status: "error", text: "Could not read export status" });
          return;
        }
        if (s.status === "COMPLETE" && s.downloadUrl) {
          window.open(s.downloadUrl, "_blank", "noopener,noreferrer");
          clearAvatarCsvExportSession(batchId);
          notify({
            status: "success",
            text: `Avatar attribute CSV ready (${s.processedRows ?? 0} rows)`,
          });
          return;
        }
        if (s.status === "FAILED") {
          clearAvatarCsvExportSession(batchId);
          notify({
            status: "error",
            text: s.errorMessage || "Avatar CSV export failed",
          });
          return;
        }
        const rows = s.processedRows ?? 0;
        if (
          (s.status === "PROCESSING" || s.status === "PENDING") &&
          rows > lastProgressRows
        ) {
          lastProgressRows = rows;
          notify({
            status: "success",
            text:
              s.status === "PENDING"
                ? "Starting avatar attribute CSV export…"
                : `Exporting avatar attributes… ${rows.toLocaleString()} rows`,
          });
        }
        await sleep(POLL_MS);
      }
      notify({ status: "error", text: "Avatar CSV export timed out" });
    },
    [batchId, notify]
  );

  const runPoll = useCallback(
    async (exportId: string) => {
      const generation = ++pollGeneration.current;
      setStarting(true);
      try {
        await pollUntilDone(exportId, generation);
      } finally {
        if (generation === pollGeneration.current) {
          setStarting(false);
        }
      }
    },
    [pollUntilDone]
  );

  useEffect(() => {
    const pending = readAvatarCsvExportSession(batchId);
    if (!pending) return;
    notify({
      status: "success",
      text: "Resuming avatar attribute CSV export…",
    });
    void runPoll(pending.exportId);
  }, [batchId, notify, runPoll]);

  const startExportForBatch = useCallback(
    async (targetBatchId: string) => {
      if (targetBatchId !== batchId) {
        notify({ status: "error", text: "Batch id mismatch" });
        return;
      }
      pollGeneration.current += 1;
      try {
        notify({
          status: "success",
          text: "Preparing avatar attribute CSV…",
        });
        const { data, errors } = await mutate({
          variables: { input: { batchId: targetBatchId } },
        });
        if (errors?.length) {
          handleGQLErrors(
            notify,
            new ApolloError({ graphQLErrors: errors })
          );
          return;
        }
        const exportId = data?.startAvatarCsvExport?.exportId;
        if (!exportId) {
          notify({ status: "error", text: "Export did not return an id" });
          return;
        }
        saveAvatarCsvExportSession(targetBatchId, exportId);
        await runPoll(exportId);
      } catch (err: any) {
        if (err?.graphQLErrors || err?.networkError) {
          handleGQLErrors(notify, err);
        } else {
          notify({
            status: "error",
            text: err?.message || "Avatar CSV export failed",
          });
        }
      }
    },
    [batchId, mutate, notify, runPoll]
  );

  return { startExportForBatch, starting };
}
